import type {
  ListExternalAccountsQuery,
  ListExternalAccountsQueryVariables,
} from 'hooks/customerApi';
import type { UseQueryResponse } from 'urql';
import type { DefaultParams, Params } from 'wouter';
import { ExternalAccountTable } from 'components/link/ExternalAccountTable';
import { LinkSummary } from 'components/link/LinkSummary';
import { TableLoadError } from 'components/TableLoadError/TableLoadError';
import { useListExternalAccountsQuery } from 'hooks/customerApi';
import { useCustomerApiContext } from 'hooks/useCustomerApiContext';
import { useDocumentTitle } from 'hooks/useDocumentTitle';
import { useHasWorkspacePermission } from 'hooks/useHasWorkspacePermission';
import React, { useCallback, useMemo, useState } from 'react';
import { getResultOrError } from 'utils/errors';
import { makePath } from 'utils/routes';
import { useLocation } from 'wouter';

import { Permissions } from '@fragment/authorization-utils/permissions';
import { PrimaryButtonV2 } from '@fragment/ui/src/components/Button/PrimaryButtonV2/PrimaryButtonV2';

import { SettingsModalLayout } from '../SchemasV2/WorkspaceSettings/SettingsModalLayout';
import { AddCustomAccountView } from './AddCustomAccountView';

export type ComponentProps = UseQueryResponse<
  ListExternalAccountsQuery,
  ListExternalAccountsQueryVariables
>[0] & {
  linkId: string;
  retry: VoidFunction;
  canAddAccount: boolean;
};

export const ExternalAccountsListViewComponent = ({
  linkId,
  data,
  error,
  fetching,
  retry,
  canAddAccount,
}: ComponentProps) => {
  const { isError, result: link } = getResultOrError(data?.link, error);
  const [, navigate] = useLocation();
  const [isAddingAccount, setIsAddingAccount] = useState(false);

  const shouldRenderAddAccountCTA =
    canAddAccount && data?.link?.__typename === 'CustomLink';

  if (isAddingAccount) {
    return (
      <AddCustomAccountView
        onClose={() => setIsAddingAccount(false)}
        linkId={linkId}
        refresh={retry}
      />
    );
  }

  return (
    <SettingsModalLayout
      title={isError ? 'Failed to load Link' : link.name}
      onBackButtonClick={() => navigate('/')}
      topButtonSection={
        shouldRenderAddAccountCTA && (
          <PrimaryButtonV2
            data-testid="add-account-button"
            onClick={() => setIsAddingAccount(true)}
            secondaryIcon="+"
          >
            Add Account
          </PrimaryButtonV2>
        )
      }
    >
      {isError ? (
        <TableLoadError
          entityColumnName="Link"
          {...{ error, retry, fetching }}
        />
      ) : (
        <>
          <LinkSummary
            link={link}
            detailsHref={makePath(['links', 'id', linkId, 'details'])}
          />
          <ExternalAccountTable
            data={link.externalAccounts.nodes}
            linkId={linkId}
          />
        </>
      )}
    </SettingsModalLayout>
  );
};

type Props<T extends DefaultParams> = {
  params: Params<T>;
};

export const ExternalAccountsListView = <T extends DefaultParams>({
  params: { linkId },
}: Props<T>) => {
  const { context } = useCustomerApiContext();
  const canAddAccount = useHasWorkspacePermission(Permissions.link.sync);
  const variables = useMemo(
    () => ({
      linkId,
    }),
    [linkId]
  );
  const [response, reExecuteQuery] = useListExternalAccountsQuery({
    context,
    pause: !context.token,
    variables,
  });
  const retry = useCallback(
    () => reExecuteQuery({ requestPolicy: 'network-only' }),
    [reExecuteQuery]
  );
  useDocumentTitle('External Accounts');
  return (
    <ExternalAccountsListViewComponent
      {...response}
      retry={retry}
      linkId={linkId}
      canAddAccount={canAddAccount}
    />
  );
};
